import { createStore, applyMiddleware, combineReducers, compose } from 'redux'
import thunkMiddleware from 'redux-thunk'
import AuthReducer, { AuthStore } from './reducers/auth'
import CalcProjectReducer, { CalcProjectStore } from './reducers/calcProject'
import ActionTypes, { Action } from './actionsTypes'

import gql from 'graphql-tag'
import { createUploadLink } from 'apollo-upload-client'
import { ApolloClient, ApolloLink, InMemoryCache } from 'apollo-boost'
import { onError } from 'apollo-link-error'
import { isTokenValid } from 'utils/tokenParser'
import { getApiUrl } from 'utils'

import isNil from 'lodash/isNil'
import get from 'lodash/get'

import { setCalcProject } from './actions/calcProject'

export interface RigipsStore {
  auth: AuthStore
  calcProject: CalcProjectStore
}

const appReducer = combineReducers({
  auth: AuthReducer,
  calcProject: CalcProjectReducer,
})

// Root Reducer
const rootReducer = (state: RigipsStore | undefined, action: Action) => {
  if (action.type === 'RESET_APP') {
    state = undefined
  }

  return appReducer(state, action)
}

const composeEnhancers = (window as any).__REDUX_DEVTOOLS_EXTENSION_COMPOSE__ || compose

export const resetApp = () => {
  return {
    type: ActionTypes.RESET_APP,
  }
}

export const CALCULATION_QUERY = gql`
  query {
    getCalculationProject {
      projectNumber
    }
  }
`

const uploadLink = createUploadLink({
  headers: {
    authorization: localStorage.getItem('token') || '',
  },
  uri: getApiUrl(),
})

const redirectToLogin = () => {
  localStorage.clear()
  window.location.href = '/login'
}

const isLoginPage = () => {
  const { pathname } = window.location
  return pathname === '/' || Boolean(pathname.match(new RegExp(`\\b(login)\\b`, 'i')))
}

const doesRequireAuth = () => {
  const { pathname } = window.location
  const pagesWithoutAuth = ['login', 'signup', 'forgot-password', 'help', 'feedback']
  return !Boolean(pathname.match(new RegExp(`\\b(${pagesWithoutAuth.join('|')})\\b`, 'i')))
}

const authMiddleware = new ApolloLink((operation: any, forward: any) => {
  const token = localStorage.getItem('token') || null

  if (token && !isTokenValid()) {
    localStorage.removeItem('token') // To be sure because there was an API Error
    localStorage.clear()
  } else {
    // Add the authorization to the headers
    if ((token && !isTokenValid() && doesRequireAuth()) || (!token && doesRequireAuth())) {
      if (!isLoginPage()) {
        return redirectToLogin()
      }
    } else {
      operation.setContext({
        headers: {
          authorization: localStorage.getItem('token') || '',
        },
      })
    }
  }

  return forward(operation)
})

const logoutLink = onError(({ graphQLErrors }: any) => {
  if (
    graphQLErrors &&
    graphQLErrors[0] &&
    (graphQLErrors[0].message === 'Invalid token' || graphQLErrors[0].message.statusCode === 401)
  ) {
    if (!isLoginPage) {
      redirectToLogin()
    }
  }
})

export const client = new ApolloClient({
  link: logoutLink.concat(authMiddleware).concat(uploadLink),
  cache: new InMemoryCache(),
})

/**
 * Store
 */
export const initializeStore = () => {
  const token = localStorage.getItem('token') || undefined
  let initialState: { auth: AuthStore; calcProject: CalcProjectStore } = {
    auth: { apiToken: undefined, authenticated: false },
    calcProject: {},
  }

  if (!isNil(token) && isTokenValid()) {
    Object.assign(initialState, {
      auth: {
        authenticated: true,
        apiToken: token,
      },
    })
  }

  const store = createStore(
    rootReducer,
    initialState,
    composeEnhancers(applyMiddleware(thunkMiddleware)),
  )

  if (!isNil(token) && isTokenValid()) {
    /*
     * Fetch the default calculation project because it's project number is
     * needed for the getPosition query
     */
    client.query({ query: CALCULATION_QUERY }).then((res) => {
      store.dispatch(setCalcProject(get(res, 'data.getCalculationProject')))
    })
  }

  return { store }
}
